import { ref, computed, watch, Ref } from "vue";

/**
 * Composable générique de recherche + pagination front-end.
 *
 * Utilisable dans n'importe quelle page contenant un tableau :
 * chaque page fournit sa propre source de données et sa propre
 * liste de colonnes/champs à filtrer (avec extraction personnalisée
 * si besoin, ex: formater une date ou traduire un statut).
 *
 * Exemple d'utilisation dans une page :
 *
 * const { searchQuery, paginatedData, filteredTotal, page, numPages,
 *         showingFrom, showingTo, goToPage } = useTableFilter(
 *   reloadings,
 *   [
 *     (r) => r.code,
 *     (r) => r.description,
 *     (r) => String(r.amount ?? ''),
 *     (r) => formatDateFr(new Date(r.createdAt)),
 *     (r) => r.user?.name,
 *     (r) => r.organization?.name,
 *     (r) => statusLabel(r.status),
 *   ],
 *   15 // taille de page
 * );
 */
export function useTableFilter<T>(
  source: Ref<T[]>,
  fieldExtractors: Array<(item: T) => string | number | null | undefined>,
  pageSize: number = 15
) {
  const searchQuery = ref("");
  const page = ref(1);
  const numPages = ref(1);

  // Filtrage sur toutes les colonnes définies par fieldExtractors
  const filteredData = computed(() => {
    const q = searchQuery.value.trim().toLowerCase();
    if (!q) {
      return source.value;
    }
    return source.value.filter((item) => {
      return fieldExtractors.some((extract) => {
        const value = extract(item);
        if (value === null || value === undefined) return false;
        return String(value).toLowerCase().includes(q);
      });
    });
  });

  const filteredTotal = computed(() => filteredData.value.length);

  // Découpage en pages de `pageSize` éléments
  const paginatedData = computed(() => {
    const start = (page.value - 1) * pageSize;
    const end = start + pageSize;
    return filteredData.value.slice(start, end);
  });

  const showingFrom = computed(() => {
    if (filteredTotal.value === 0) return 0;
    return (page.value - 1) * pageSize + 1;
  });

  const showingTo = computed(() => {
    return Math.min(page.value * pageSize, filteredTotal.value);
  });

  function goToPage(numPage: number) {
    page.value = numPage;
  }

  // Recalcule le nombre de pages à chaque changement du résultat filtré
  watch(
    filteredTotal,
    (newTotal) => {
      numPages.value = Math.max(1, Math.ceil(newTotal / pageSize));
      if (page.value > numPages.value) {
        page.value = numPages.value;
      }
    },
    { immediate: true }
  );

  // Revenir à la page 1 à chaque nouvelle recherche
  watch(searchQuery, () => {
    page.value = 1;
  });

  return {
    searchQuery,
    filteredData,
    filteredTotal,
    paginatedData,
    page,
    numPages,
    pageSize,
    showingFrom,
    showingTo,
    goToPage,
  };
}